import { useGetCurrentOrg, useUpdateCurrentOrg, type OrgOut } from '@myrentalmate/api-client';
import { Alert, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import type { FormEvent } from 'react';

import {
  addressFields,
  EMAIL_FIELD,
  FormFields,
  optional,
  PHONE_FIELD,
  required,
  useFormValues,
  type Field,
  type FormValues,
} from '../../components/FormFields';
import { okData } from '../../lib/apiData';
import { TIMEZONES } from '../../lib/timezones';
import { apiErrorMessage } from '../../lib/apiError';
import { brand } from '../../lib/brand';
import { formatPhone } from '../../lib/contact';
import { LogoSection } from './LogoSection';

const CONTACT_FIELDS: Field[] = [
  { name: 'name', label: 'Business Name', required: true, helperText: 'Shown across the app' },
  PHONE_FIELD,
  { ...EMAIL_FIELD, label: 'Business email' },
  { name: 'website', label: 'Website', type: 'website' },
  {
    name: 'license_number',
    label: 'License Number',
    helperText: 'Your real estate or property management license, if you have one',
  },
];
const ADDRESS_FIELDS = addressFields();

function regionFields(current: string): Field[] {
  // An org created with another timezone keeps it as a choice, so saving can't silently change it.
  const known = TIMEZONES.some((t) => t.value === current);
  return [
    {
      name: 'timezone',
      label: 'Timezone',
      type: 'select',
      required: true,
      helperText: 'Decides when "today" starts for due dates and expiry dates',
      options: known ? TIMEZONES : [...TIMEZONES, { value: current, label: current }],
    },
  ];
}

function toValues(org: OrgOut): FormValues {
  return {
    name: org.name,
    timezone: org.timezone,
    phone: formatPhone(org.phone),
    email: org.email ?? '',
    website: org.website ?? '',
    license_number: org.license_number ?? '',
    address_line1: org.address_line1 ?? '',
    address_line2: org.address_line2 ?? '',
    city: org.city ?? '',
    state: org.state ?? '',
    postal_code: org.postal_code ?? '',
  };
}

function BusinessForm({ org }: { org: OrgOut }) {
  const queryClient = useQueryClient();
  const region = regionFields(org.timezone);
  const fields = [...CONTACT_FIELDS, ...ADDRESS_FIELDS, ...region];
  const form = useFormValues(fields, toValues(org));
  const save = useUpdateCurrentOrg({
    mutation: {
      onSuccess: (response) => {
        const saved = okData(response);
        if (saved) form.setValues(toValues(saved));
        return queryClient.invalidateQueries(); // the top bar shows the business name
      },
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!form.validate()) return;
    const { values } = form;
    save.mutate({
      data: {
        name: required(values, 'name'),
        timezone: required(values, 'timezone'),
        phone: optional(values, 'phone'),
        email: optional(values, 'email'),
        website: optional(values, 'website'),
        license_number: optional(values, 'license_number'),
        address_line1: optional(values, 'address_line1'),
        address_line2: optional(values, 'address_line2'),
        city: optional(values, 'city'),
        state: optional(values, 'state'),
        postal_code: optional(values, 'postal_code'),
      },
    });
  }

  const group = (title: string, groupFields: Field[]) => (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6">{title}</Typography>
      <FormFields
        fields={groupFields}
        values={form.values}
        errors={form.errors}
        onChange={form.onChange}
      />
    </Paper>
  );

  return (
    <Stack component="form" spacing={3} onSubmit={submit} sx={{ maxWidth: 640 }}>
      {group('Business details', CONTACT_FIELDS)}
      {group('Business address', ADDRESS_FIELDS)}
      {group('Region', region)}
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6">Your Sign-In Page</Typography>
        <Typography color="text.secondary">
          Share this link so your team sees your business name when signing in. It stays the same if
          you change your business name.
        </Typography>
        <Typography sx={{ mt: 1, overflowWrap: 'anywhere' }}>
          {brand.appUrl}/o/{org.slug}
        </Typography>
      </Paper>
      {save.isError && <Alert severity="error">{apiErrorMessage(save.error)}</Alert>}
      {save.isSuccess && <Alert severity="success">Saved.</Alert>}
      <div>
        <Button type="submit" variant="contained" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </Stack>
  );
}

export function BusinessSettingsPage() {
  const org = okData(useGetCurrentOrg().data);
  return (
    <Stack spacing={2}>
      <LogoSection />
      {org ? <BusinessForm key={org.id} org={org} /> : <CircularProgress aria-label="Loading" />}
    </Stack>
  );
}
